import { DateTime } from 'luxon';
import { advancedStringFilter } from 'src/app/util';
import { LastTrack, User, UserGroup, UserRole } from 'src/models';
import { UserApiParams } from '../user/user.service';
import { MockDataStore, User as MockStoreUser } from './mock-data-store';
import { DEMO_TIMELINE_PROJECTS } from './demo-timeline-projects';

export const DEMO_REPORT_GROUP_IDS = {
  engineering: 'tag-1',
  marketing: 'tag-2',
  design: 'tag-3',
} as const;

/** Department names for executive dashboard and group-level mock widgets. */
export const DEMO_TEAM_NAMES = [
  'Engineering',
  'Marketing',
  'Design',
  'Sales',
  'Customer Support',
] as const;

/** Stable demo team/tag IDs (includes groups beyond those present on mockUsers). */
export const DEMO_TEAM_IDS = [
  DEMO_REPORT_GROUP_IDS.engineering,
  DEMO_REPORT_GROUP_IDS.marketing,
  DEMO_REPORT_GROUP_IDS.design,
  'tag-4',
  'tag-5',
] as const;

export function getDemoTeamName(index: number): string {
  const normalized = ((index % DEMO_TEAM_NAMES.length) + DEMO_TEAM_NAMES.length) % DEMO_TEAM_NAMES.length;
  return DEMO_TEAM_NAMES[normalized];
}

export function getDemoTeamId(index: number): string {
  const normalized = ((index % DEMO_TEAM_IDS.length) + DEMO_TEAM_IDS.length) % DEMO_TEAM_IDS.length;
  return DEMO_TEAM_IDS[normalized];
}

/** Roles used by Groups managers list and other role-filtered pickers under sample data. */
const DEMO_REPORT_ROLE_BY_USER_ID: Record<string, UserRole> = {
  'user-1': 'manager',
  'user-4': 'admin',
};

/**
 * Presence used by Timeline `getUserDetails`. Without `lastTrack`, active demo
 * users always render as "Never Tracked Time" even when mock worklogs exist.
 */
const DEMO_LAST_TRACK_BY_USER_ID: Record<string, { online: boolean, status: string, hoursAgo: number }> = {
  'user-1': { online: true, status: 'computer', hoursAgo: 0 },
  'user-2': { online: true, status: 'paidBreak', hoursAgo: 0 },
  'user-3': { online: false, status: 'computer', hoursAgo: 2 },
  'user-4': { online: false, status: 'computer', hoursAgo: 26 },
  'user-5': { online: true, status: 'mobile', hoursAgo: 0 },
  'user-silent-1': { online: true, status: 'computer', hoursAgo: 0 },
};

export function buildDemoReportLastTrack(userId: string, index = 0): LastTrack {
  const variant = DEMO_LAST_TRACK_BY_USER_ID[userId] ?? {
    online: false,
    status: 'computer',
    hoursAgo: 3,
  };
  const at = DateTime.utc().minus({ hours: variant.hoursAgo }).toISO();
  const project = DEMO_TIMELINE_PROJECTS[index % DEMO_TIMELINE_PROJECTS.length];
  const onBreak = variant.online && variant.status === 'paidBreak';

  return {
    ip: `192.168.1.${10 + (index % 200)}`,
    online: variant.online,
    updatedAt: at,
    activeAt: at,
    clientVersion: '3.15.0',
    deviceId: 'demo-timeline-device',
    projectId: project.id,
    projectName: project.name,
    status: variant.status,
    taskId: `task-demo-${userId}`,
    taskName: onBreak ? 'Break' : 'Deep work',
  };
}

export function mapMockStoreUserToReportUser(mockUser: MockStoreUser, index = 0): User {
  return {
    id: mockUser.id,
    name: mockUser.name,
    email: mockUser.email,
    employeeId: `EMP-${mockUser.id.replace('user-', '')}`,
    tagIds: mockUser.tagId ? [mockUser.tagId] : [],
    showOnReports: true,
    active: !mockUser.archived,
    archived: mockUser.archived,
    role: DEMO_REPORT_ROLE_BY_USER_ID[mockUser.id] ?? 'user',
    lastTrack: buildDemoReportLastTrack(mockUser.id, index),
  } as User;
}

export function buildDemoReportUsers(): User[] {
  return MockDataStore.getInstance().getAllUsers().map(mapMockStoreUserToReportUser);
}

export function buildDemoReportGroups(): UserGroup[] {
  const users = MockDataStore.getInstance().getAllUsers();
  const tagMap = new Map<string, { name: string, count: number, managers: string[] }>();

  users.forEach(user => {
    if (!user.tagId) {
      return;
    }
    const existing = tagMap.get(user.tagId) || { name: user.tagName, count: 0, managers: [] as string[] };
    existing.count += 1;
    if (DEMO_REPORT_ROLE_BY_USER_ID[user.id] === 'manager') {
      existing.managers.push(user.id);
    }
    tagMap.set(user.tagId, existing);
  });

  const now = new Date().toISOString();
  return Array.from(tagMap.entries()).map(([id, { name, count, managers }]) => ({
    id,
    name,
    createdAt: now,
    creatorId: '',
    deleted: false,
    description: '',
    managedUsers: count,
    managedUsersOnReports: count,
    managers,
    modifiedAt: now,
    readOnly: false,
    selfUser: 0,
    special: '',
    users: count,
    usersOnReports: count,
    ssoEnforcement: { enabled: false },
  }));
}

export const DEMO_REPORT_ALL_USERS_FILTERS = new Set(['all-on-reports', 'all-self-on-reports']);

const DEMO_TEAM_ID_SET = new Set<string>(DEMO_TEAM_IDS);

/** True for catalog mock-store user ids (`user-1`, `user-silent-1`, …), not live Time Doctor ids. */
export function isDemoReportUserId(id: string | null | undefined): boolean {
  if (!id) {
    return false;
  }
  return MockDataStore.getInstance().getAllUsers().some(user => user.id === id);
}

/** True for catalog mock group/tag ids (`tag-1` … `tag-5`). */
export function isDemoReportGroupId(id: string | null | undefined): boolean {
  return !!id && DEMO_TEAM_ID_SET.has(id);
}

function asIdList(value: string | string[] | undefined | null): string[] {
  if (value == null || value === '') {
    return [];
  }
  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean);
  }
  return String(value).split(',').filter(Boolean);
}

/**
 * True when live API params still carry an explicit mock user or group id.
 * `all-on-reports` / `all-self-on-reports` are not catalog ids.
 */
export function apiParamsContainDemoFilter(params?: {
  user?: string | string[];
  tag?: string | string[];
  'filter[id]'?: string | string[];
} | null): boolean {
  if (!params) {
    return false;
  }
  const users = asIdList(params.user).filter(id => !DEMO_REPORT_ALL_USERS_FILTERS.has(id));
  const filterIds = asIdList(params['filter[id]']);
  const tags = asIdList(params.tag);
  return users.some(isDemoReportUserId) || filterIds.some(isDemoReportUserId) || tags.some(isDemoReportGroupId);
}

export interface DemoCatalogSelection {
  type?: 'users' | 'groups';
  all?: boolean;
  list?: string[];
}

/**
 * True when a picker selection includes catalog ids that must be dropped after sample data is dismissed.
 * `all: true` is left alone — that filter is valid for both mock and live lists.
 */
export function selectionContainsDemoIds(selection?: DemoCatalogSelection | null): boolean {
  if (!selection || selection.all) {
    return false;
  }
  const list = (selection.list || []).filter(Boolean);
  if (!list.length) {
    return false;
  }
  if (selection.type === 'groups') {
    return list.some(isDemoReportGroupId);
  }
  return list.some(isDemoReportUserId);
}

function resolveDemoReportUserIds(user: UserApiParams['user']): string[] | null {
  if (user == null || user === '') {
    return null;
  }

  if (typeof user === 'string' && DEMO_REPORT_ALL_USERS_FILTERS.has(user)) {
    return null;
  }

  const ids = Array.isArray(user)
    ? user.map(String).filter(Boolean)
    : String(user).split(',').filter(Boolean);

  if (ids.length === 0) {
    return [];
  }

  if (ids.every(id => DEMO_REPORT_ALL_USERS_FILTERS.has(id))) {
    return null;
  }

  return ids.filter(id => !DEMO_REPORT_ALL_USERS_FILTERS.has(id));
}

export function filterDemoReportUsers(
  users: User[],
  options?: UserApiParams,
  search?: string,
): User[] {
  let filtered = users.filter(user => user.showOnReports !== false);

  const filterId = options?.['filter[id]'];
  if (filterId?.length) {
    const ids = Array.isArray(filterId) ? filterId : [filterId];
    filtered = filtered.filter(user => ids.includes(user.id));
  }

  if (options?.tag) {
    const tags = Array.isArray(options.tag) ? options.tag : `${options.tag}`.split(',');
    filtered = filtered.filter(user => user.tagIds?.some(tagId => tags.includes(tagId)));
  }

  if (options?.['filter[role]']) {
    const roles = Array.isArray(options['filter[role]'])
      ? options['filter[role]']
      : [options['filter[role]']];
    filtered = filtered.filter(user => roles.includes(user.role));
  }

  if (options?.['filter[!role]']) {
    const excludedRoles = Array.isArray(options['filter[!role]'])
      ? options['filter[!role]']
      : [options['filter[!role]']];
    filtered = filtered.filter(user => !excludedRoles.includes(user.role));
  }

  if (!options?.['include-archived-users']) {
    filtered = filtered.filter(user => !user.archived);
  }

  const keyphrase = search || options?.['filter[keyphrase]'];
  if (keyphrase) {
    filtered = advancedStringFilter(filtered, `${keyphrase}`, user => [user.name, user.email, user.employeeId]);
  }

  const userIds = resolveDemoReportUserIds(options?.user);
  if (userIds !== null) {
    filtered = filtered.filter(user => userIds.includes(user.id));
  }

  return filtered;
}
